import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import FilterDrawer from '../components/FilterDrawer';
import { getFirstImage } from '../utils/imageHelper';
import ListingImage from '../components/ListingImage';
import { Edit, Trash2, Plus, Grid3x3, List, Search, SlidersHorizontal } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { getListingTabs, filterListingsByTab, getSortingOptions, sortListings } from '../utils/listingTabsConfig';
import { getListingFilters, applyListingFilters } from '../utils/listingFiltersConfig';

// Mirrors backend RENTAL_PERIOD_SUFFIX (utils/listing_price.py) — label shown
// next to the canonical aniyor_price_unit suffix computed by the pricing engine.
const RENTAL_UNIT_LABELS = {
  '/hr': 'Hourly Rate',
  '/day': 'Daily Rate',
  '/night': 'Nightly Rate',
  '/wk': 'Weekly Rate',
  '/mo': 'Monthly Rate',
  '/yr': 'Yearly Rate',
  '/unit': 'Rate',
  '/event': 'Price',
};

const Rentals = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { apiCall } = useAuth();
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('updated_desc');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ selectedFilters: {}, rangeValues: {} });

  useEffect(() => {
    fetchRentals();
  }, []);

  const fetchRentals = async () => {
    try {
      setLoading(true);
      const data = await apiCall(`/api/listings/?listing_type=rental`);
      setRentals(data);
    } catch (error) {
      console.error('[Rentals] Fetch error:', error);
      toast({ title: 'Error', description: 'Failed to fetch rentals', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this rental?')) return;

    try {
      await apiCall(`/api/listings/${id}`, { method: 'DELETE' });
      setRentals(rentals.filter((r) => r.id !== id));
      toast({ title: 'Success', description: 'Rental deleted successfully' });
    } catch (error) {
      console.error('[Rentals] Delete error:', error);
      toast({ title: 'Error', description: 'Failed to delete rental', variant: 'destructive' });
    }
  };

  // Get filtered and sorted rentals
  const getDisplayRentals = () => {
    let filtered = rentals;

    // Apply tab filter
    filtered = activeTab === 'all' ? filtered : filterListingsByTab(filtered, activeTab);

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.title?.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query) ||
        r.category?.toLowerCase().includes(query)
      );
    }

    // Apply filters from drawer
    filtered = applyListingFilters(filtered, appliedFilters, 'Rentals');

    // Apply sorting
    return sortListings(filtered, sortBy);
  };

  const displayRentals = getDisplayRentals();
  const tabs = getListingTabs(rentals);
  const activeFiltersCount = Object.values(appliedFilters.selectedFilters).flat().length + 
    Object.keys(appliedFilters.rangeValues).length;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-3">
                <button
                  onClick={() => navigate('/listings')}
                  className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800"
                  title="Back to All Listings"
                  data-testid="back-to-listings-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="m12 19-7-7 7-7"/></svg>
                </button>
                <h1 className="text-3xl font-bold text-gray-900">Rentals</h1>
              </div>
            <p className="text-gray-600 mt-2">Manage your rental listings</p>
          </div>
          <div className="flex gap-2">
            <div className="flex border rounded-lg">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={() => navigate('/listings/rentals/new')}>
              <Plus className="h-4 w-4 mr-2" /> Create Rental
            </Button>
          </div>
        </div>

        {/* Tabs, Search, Filters, Sorting */}
        <Card>
          <CardContent className="p-4 md:p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-4">
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
                <TabsList className="inline-flex md:grid md:grid-cols-6 md:w-full md:max-w-4xl gap-1 min-w-max md:min-w-0">
                  {tabs.map(tab => (
                    <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                      <tab.icon className="h-3 w-3 md:h-4 md:w-4" />
                      {tab.label} ({tab.count})
                    </TabsTrigger>
                  ))}
                  <TabsTrigger value="all" className="flex items-center gap-1 text-xs md:text-sm whitespace-nowrap px-3 py-2">
                    All ({rentals.length})
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>

            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search rentals by title, description, category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <Button 
                variant="outline" 
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2"
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  {getSortingOptions().map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Filter Drawer */}
        <FilterDrawer
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          filters={getListingFilters(rentals, 'Rentals')}
          onApplyFilters={setAppliedFilters}
          activeFiltersCount={activeFiltersCount}
        />

        {displayRentals.length === 0 ? (
          <Card className="p-12 text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No rentals yet</h3>
            <p className="text-gray-600 mb-4">Create your first rental listing to get started</p>
            <Button onClick={() => navigate('/listings/rentals/new')}>
              <Plus className="h-4 w-4 mr-2" /> Create Rental
            </Button>
          </Card>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayRentals.map((rental) => (
              <Card key={rental.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/listings/rentals/${rental.id}`)}>
                <ListingImage
                  src={getFirstImage(rental.media)}
                  alt={rental.title}
                  className="w-full h-48 object-cover"
                  placeholderContent={<span className="text-gray-400">No image</span>}
                />
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg">{rental.title}</h3>
                    <Badge variant={rental.status === 'active' ? 'default' : 'secondary'}>
                      {rental.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{rental.description}</p>
                  <div className="flex justify-between items-center">
                    <div>
                      {(() => {
                        // v8 fix: read the canonical pricing-engine output
                        // (aniyor_listing_price / aniyor_price_unit) instead of
                        // re-deriving price from raw `data` fields — the engine
                        // already handles pricingModel (incl. per_night) AND
                        // Hotel room-type-based pricing correctly.
                        const price = rental.aniyor_listing_price ?? 0;
                        const unitSuffix = rental.aniyor_price_unit || '';
                        const unitLabel = RENTAL_UNIT_LABELS[unitSuffix] || '';
                        if (price === 0 && !unitSuffix) {
                          return (
                            <p className="text-sm text-gray-400 italic" data-testid={`rental-card-price-${rental.id}`}>
                              Price not set
                            </p>
                          );
                        }
                        return (
                          <>
                            {unitLabel && <span className="text-sm text-gray-500">{unitLabel}:</span>}
                            <p className="font-semibold text-lg" data-testid={`rental-card-price-${rental.id}`}>
                              ₹{Number(price).toLocaleString('en-IN')}{unitSuffix}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/listings/rentals/edit/${rental.id}`);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => handleDelete(rental.id, e)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-gray-700">Rental</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Category</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Rate</th>
                  <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                  <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRentals.map((rental) => (
                  <tr
                    key={rental.id}
                    className="border-b hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/listings/rentals/${rental.id}`)}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <ListingImage
                          src={getFirstImage(rental.media)}
                          alt={rental.title}
                          className="w-12 h-12 object-cover rounded flex-shrink-0"
                          placeholderContent={<span className="text-xs text-gray-400">No img</span>}
                        />
                        <div>
                          <p className="font-medium">{rental.title}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{rental.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline">{rental.category}</Badge>
                    </td>
                    <td className="p-4">
                      <span className="font-semibold" data-testid={`rental-row-price-${rental.id}`}>
                        ₹{Number(rental.aniyor_listing_price ?? 0).toLocaleString('en-IN')}{rental.aniyor_price_unit || ''}
                      </span>
                    </td>
                    <td className="p-4">
                      <Badge variant={rental.status === 'active' ? 'default' : 'secondary'}>
                        {rental.status}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/listings/rentals/edit/${rental.id}`);
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => handleDelete(rental.id, e)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Rentals;
